import { inject, Injectable } from '@angular/core';

import { DealerProvider } from '../../../../../../auth/dealer/dealer.provider';
import {
  CustomerSalutation,
  CustomerTitle,
  DealerConsent,
  NationalIdDocumentType,
} from '../../../../model/customer-information.model';
import { RetailCrmSalutation } from '../../model/requests/salutation.model';
import { RetailCrmTitle } from '../../model/requests/title.model';
import { RetailCrmPrivacyConsent } from '../../model/responses/account-details.model';
import {
  RetailCrmNationalIdDocumentType,
  RetailCrmNationalIdDocumentTypeForWrite,
} from '../../model/responses/national-id-document-type.model';

@Injectable({
  providedIn: 'root',
})
export class RetailCrmCustomerFieldsRequestModelMapper {
  private readonly dealerProvider = inject(DealerProvider);

  private static readonly GbIeMarkets = new Set(['GB', 'IE']);

  private isGbOrIeMarket(): boolean {
    const country = this.dealerProvider.activeCountry();
    return !!country && RetailCrmCustomerFieldsRequestModelMapper.GbIeMarkets.has(country.toUpperCase());
  }

  mapToNationalIdDocumentType(
    documentType: NationalIdDocumentType | undefined,
  ): RetailCrmNationalIdDocumentType | undefined {
    switch (documentType) {
      case NationalIdDocumentType.DRIVER_LICENCE:
        return RetailCrmNationalIdDocumentType.DRIVER_LICENSE;
      case NationalIdDocumentType.DRIVER_LICENSE_ID:
        return RetailCrmNationalIdDocumentType.DRIVER_LICENSE_ID;
      case NationalIdDocumentType.IDENTITY_CARD:
        return RetailCrmNationalIdDocumentType.ID_CARD;
      case NationalIdDocumentType.IDENTIFICATION_NUMBER:
        return RetailCrmNationalIdDocumentType.IDENTIFICATION_NUMBER;
      case NationalIdDocumentType.PASSPORT:
        return RetailCrmNationalIdDocumentType.PASSPORT;
      case NationalIdDocumentType.PASSPORT_ID:
        return RetailCrmNationalIdDocumentType.PASSPORT_ID;
      case NationalIdDocumentType.PERSON_ID:
        return RetailCrmNationalIdDocumentType.PERSON_ID_CARD;
      case NationalIdDocumentType.SOCIAL_SECURITY_NUMBER:
        return RetailCrmNationalIdDocumentType.SOCIAL_SECURITY_NUMBER;
      case NationalIdDocumentType.SOUTH_AFRICA_NATIONAL_ID:
        return RetailCrmNationalIdDocumentType.SOUTH_AFRICA_NATIONAL_ID;
      default:
        return undefined;
    }
  }

  mapToRetailCrmTitle(customerTitle: CustomerTitle | undefined): RetailCrmTitle | undefined {
    switch (customerTitle) {
      case CustomerTitle.DR:
        return RetailCrmTitle.DR;
      case CustomerTitle.PROF:
        return RetailCrmTitle.PROF;
      case CustomerTitle.PROF_DR:
        return RetailCrmTitle.PROF_DR;
      default:
        return undefined;
    }
  }

  /** Write-path counterpart of {@link mapToRetailCrmTitle}: a missing title is sent as an explicit
   * "none" rather than omitted.
   * On create the two are equivalent - there is nothing to overwrite - but on update and sync
   * Retail CRM merges, and `undefined` is dropped by JSON serialisation, so clearing the title in
   * the dialog had no effect at all. Verified against dev: Retail CRM removes the field when "" is
   * sent, the same behaviour GCDM shows for its own title (see GcdmCustomerRequestMapper.toUpdateCustomerRequest).
   */
  mapToRetailCrmTitleForWrite(customerTitle: CustomerTitle | undefined): RetailCrmTitle {
    return this.mapToRetailCrmTitle(customerTitle) ?? RetailCrmTitle.NONE;
  }

  /** Write-path counterpart of {@link mapToNationalIdDocumentType}: a cleared type is sent as an
   * explicit "" rather than omitted. See {@link mapToRetailCrmTitleForWrite} for the rationale. */
  mapToNationalIdDocumentTypeForWrite(
    documentType: NationalIdDocumentType | undefined,
  ): RetailCrmNationalIdDocumentTypeForWrite {
    return this.mapToNationalIdDocumentType(documentType) ?? '';
  }

  mapToRetailCrmSalutation(customerSalutation: CustomerSalutation | undefined): RetailCrmSalutation | undefined {
    switch (customerSalutation) {
      case CustomerSalutation.MR:
        return RetailCrmSalutation.MR;
      case CustomerSalutation.MS:
        // GB/IE has a dedicated ANMS code; other markets use ANFRA for Ms.
        return this.isGbOrIeMarket() ? RetailCrmSalutation.MS : RetailCrmSalutation.MRS;
      case CustomerSalutation.MX:
        return RetailCrmSalutation.MX;
      case CustomerSalutation.MRS:
        return RetailCrmSalutation.MRS;
      case CustomerSalutation.MISS:
        return RetailCrmSalutation.MISS;
      case CustomerSalutation.DR:
        return RetailCrmSalutation.DR;
      default:
        return RetailCrmSalutation.NONE;
    }
  }

  mapPrivacyConsent(dealerConsent: DealerConsent | undefined): RetailCrmPrivacyConsent | undefined {
    if (!dealerConsent) {
      return;
    }

    return {
      consentId: dealerConsent.consentId,
      consentDate: dealerConsent.consentDate,
      legalEntities: dealerConsent.legalEntities,
      dataProcessing: dealerConsent.consent.dataProcessing,
      customerProfile: dealerConsent.consent.customerProfile,
      contactChannels: {
        email: dealerConsent.consent.email,
        inCar: dealerConsent.consent.inCar,
        letter: dealerConsent.consent.letter,
        phoneMessenger: dealerConsent.consent.phoneMessenger,
      },
    };
  }
}
